import React, { useState, useEffect } from "react";
import jsPDF from "jspdf";
import { buildApiUrl } from '../config';
import "./invoices.css";
import stampImage from "../assets/stamp.png";
import signImage from "../assets/sign.png";

const Invoices = () => {
  const [showAllButtons, setShowAllButtons] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [ledgerData, setLedgerData] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [showInvoiceNumberField, setShowInvoiceNumberField] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [invoiceMonth, setInvoiceMonth] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredLedgerData, setFilteredLedgerData] = useState([]);

  // Physical Invoice States
  const [showPhysicalInvoiceModal, setShowPhysicalInvoiceModal] = useState(false);
  const [physicalInvoiceData, setPhysicalInvoiceData] = useState({
    invoice_date: new Date().toISOString().split('T')[0],
    company_name: "",
    invoice_number: "",
    amount: "",
    received_amount: "0",
    notes: ""
  });
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState("");

  // Radio Button States
  const [activeView, setActiveView] = useState("generate"); // "generate" or "company"
  const [physicalInvoices, setPhysicalInvoices] = useState([]);
  const [loadingPhysicalInvoices, setLoadingPhysicalInvoices] = useState(false);

  // WhatsApp and Email States
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [receiverEmail, setReceiverEmail] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");

  useEffect(() => {
    console.log("🚀 Invoices component mounted - useEffect running");
    console.log("🔍 Starting to fetch companies...");
    fetch(buildApiUrl("api/companiesinfo"))
      .then((response) => {
        console.log("📡 Response received, status:", response.status);
        return response.json();
      })
      .then((data) => {
        console.log("📊 Companies data:", data);
        console.log("📊 Number of companies:", data.length);
        setCompanies(data);
        console.log("✅ setCompanies called");
      })
      .catch((error) => {
        console.error("❌ Error fetching companies:", error);
      });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault();
        setShowAllButtons(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleShowAvailedServices = async () => {
    if (!selectedCompany) {
      alert("Please select a company first!");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        buildApiUrl(`api/ledgerEntries?entityType=company&entityName=${encodeURIComponent(
          selectedCompany
        )}`)
      );
      const data = await response.json();

      if (response.ok) {
        setLedgerData(data);
        setFilteredLedgerData(data);
        setShowInvoiceNumberField(true);
        console.log("Fetched Data:", data); // Log fetched data to check date format
        const company = companies.find(
          (company) => company.name === selectedCompany
        );
        setCompanyInfo(company || {});
      } else {
        console.error("Failed to fetch ledger entries:", data.error);
      }
    } catch (error) {
      console.error("Error fetching ledger entries:", error);
    }

    setLoading(false);
  };
  const handleFilterByDate = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }

    // Helper function to normalize date from "DD-MMM-YYYY" to "YYYY-MM-DD"
    const convertDateToISO = (dateStr) => {
      const months = {
        Jan: "01",
        Feb: "02",
        Mar: "03",
        Apr: "04",
        May: "05",
        Jun: "06",
        Jul: "07",
        Aug: "08",
        Sep: "09",
        Oct: "10",
        Nov: "11",
        Dec: "12",
      };
      const [day, month, year] = dateStr.split("-");
      return `${year}-${months[month]}-${day.padStart(2, "0")}`;
    };

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Filter ledgerData based on date range
    const filteredData = ledgerData.filter((entry) => {
      const rawEntryDate = entry.Date;
      const isoEntryDate = convertDateToISO(rawEntryDate);

      const entryDate = new Date(isoEntryDate);

      // Ensure date is within range (inclusive of start and end dates)
      return entryDate >= start && entryDate <= end;
    });

    if (filteredData.length === 0) {
      console.log("No records found for the selected date range.");
      alert("No records found for the selected date range.");
    } else {
      console.log(`Filtered records count: ${filteredData.length}`);
    }

    setFilteredLedgerData(filteredData);
  };

  const generateOnePageInvoice = (type) => {
    if (filteredLedgerData.length === 0) {
      alert("No filtered data available to generate an invoice.");
      return;
    }

    const doc = new jsPDF();

    // Header Section
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text("AL MAMZAR CARS WASHING LLC", 105, 20, { align: "center" });
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Vehicle Service Station", 105, 28, { align: "center" });

    doc.setFont("helvetica", "bold");
    // Invoice Details
    doc.line(10, 35, 200, 35);
    doc.setFontSize(10);
    doc.text(`Date: ${new Date().toLocaleDateString("en-GB")}`, 10, 40);
    doc.text(`Invoice Month: ${invoiceMonth || "N/A"}`, 100, 40, { align: "center" }); // Invoice Month in the center

    const invoiceType =
      type === "withVAT"
        ? "Tax Invoice"
        : type === "withoutVAT"
          ? "Invoice"
          : "Invoice";
    doc.text(`${invoiceType} No: ${invoiceNumber || "N/A"}`, 200, 40, { align: "right" });

    // Our Company Details Box
    doc.text("AlMamzar Car Wash Details:", 10, 50);
    doc.setFont("helvetica", "normal");
    doc.text("Warehouse No. 2, P.O Box: 63798 Jurf Ajman", 10, 55);
    doc.text("RAK Bank Account number : 8193410093901", 10, 60);
    doc.text("TRN: 104630932200003", 10, 65);
    doc.text("IBAN: AE710400008193410093901", 10, 70);
    doc.text("Tel: +971 50 367 2862", 10, 75);
    doc.text("Email: almamzarcarwash@gmail.com", 10, 80);
    doc.rect(8, 45, 95, 40);

    // Client Details Box
    doc.setFont("helvetica", "bold");
    doc.text("Client Details:", 110, 50);
    doc.setFont("helvetica", "normal");
    const { name, address, contactPerson, trnNo, contactNo } = companyInfo;
    doc.text(`Contact Person: ${contactPerson || "N/A"}`, 110, 55);

    const wrappedAddress = doc.splitTextToSize(`Address: ${address || "N/A"}`, 85);
    wrappedAddress.forEach((line, index) => doc.text(line, 110, 65 + index * 5));

    const contactPositionY = 65 + wrappedAddress.length * 5;
    doc.text(`Contact No: ${contactNo || "N/A"}`, 110, contactPositionY);

    const trnPositionY = contactPositionY + 5;
    doc.text(`TRN: ${trnNo || "N/A"}`, 110, trnPositionY);

    const rectHeight = trnPositionY - 45 + 5;
    doc.rect(108, 45, 92, rectHeight);

    // Ledger Summary Table with refined design
    const tableStartY = 100;
    const rowHeight = 10;
    const colDescriptionX = 15;
    const colAmountX = 180; // Adjusted to bring the Total Amount to the right
    const tableWidth = 190;

    // Draw Table Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.rect(10, tableStartY, tableWidth, rowHeight); // Header box
    doc.text("Description", colDescriptionX, tableStartY + 7); // Adjusted vertical alignment
    doc.text("Total Amount (AED)", colAmountX, tableStartY + 7, { align: "right" });
    var description = "Vehicle washing, greasing and polishing";
    // Draw Table Rows
    doc.setFont("helvetica", "normal");
    if (companyInfo.name === "BCI International FZE") {
      description = "Container Washing";
    }
    // Get the final balance from the last entry in the ledger (this is the outstanding balance)
    const finalBalance = filteredLedgerData.length > 0
      ? parseFloat(filteredLedgerData[filteredLedgerData.length - 1].Balance) || 0
      : 0;

    // Total is now the final balance from ledger
    const total = finalBalance;

    // Calculate Subtotal and VAT based on the total
    let subtotal, vatAmount, adjustedTotal;

    if (type === "withVAT") {
      // For withVAT: Total = Subtotal + VAT, so Subtotal = Total / 1.05
      subtotal = total / 1.05;
      vatAmount = subtotal * 0.05;
      adjustedTotal = subtotal; // For display in description row
    } else {
      // For normal and withoutVAT: Total = Subtotal (no VAT)
      subtotal = total;
      vatAmount = 0;
      adjustedTotal = total;
    }

    const tableContentY = tableStartY + rowHeight;
    doc.rect(10, tableContentY, tableWidth, rowHeight); // Content row box
    doc.text(description, colDescriptionX, tableContentY + 7); // Adjusted vertical alignment
    doc.text(adjustedTotal.toFixed(2), colAmountX, tableContentY + 7, { align: "right" });

    // Total, Subtotal, VAT Boxes (Enclosed in a single boundary)
    let currentY = tableContentY + rowHeight + 5;
    doc.setFont("helvetica", "bold");

    if (type === "withVAT") {
      // Draw outer boundary for Subtotal, VAT, and Total
      const boxWidth = 50;
      const boxStartX = colAmountX - 40; // Start position for the boundary
      const boxStartY = currentY;
      const totalRowsHeight = rowHeight * 3; // 3 rows
      doc.rect(boxStartX, boxStartY, boxWidth, totalRowsHeight); // Outer boundary

      // Subtotal row
      doc.line(boxStartX, currentY + rowHeight, boxStartX + boxWidth, currentY + rowHeight); // Row separator
      doc.text("Subtotal:", boxStartX + 5, currentY + rowHeight - 3); // Inside the box
      doc.text(`${subtotal.toFixed(2)}`, boxStartX + boxWidth - 5, currentY + rowHeight - 3, {
        align: "right",
      });

      currentY += rowHeight;

      // VAT row
      doc.line(boxStartX, currentY + rowHeight, boxStartX + boxWidth, currentY + rowHeight); // Row separator
      doc.text("VAT (5%):", boxStartX + 5, currentY + rowHeight - 3); // Inside the box
      doc.text(`${vatAmount.toFixed(2)}`, boxStartX + boxWidth - 5, currentY + rowHeight - 3, {
        align: "right",
      });

      currentY += rowHeight;

      // Total row
      doc.text("Total:", boxStartX + 5, currentY + rowHeight - 3); // Inside the box
      doc.text(`${total.toFixed(2)}`, boxStartX + boxWidth - 5, currentY + rowHeight - 3, {
        align: "right",
      });
    } else {
      // Single row for Total only
      const boxWidth = 50;
      const boxStartX = colAmountX - 40; // Start position for the box
      const boxStartY = currentY;

      doc.rect(boxStartX, boxStartY, boxWidth, rowHeight); // Outer boundary
      doc.text("Total:", boxStartX + 5, currentY + rowHeight - 3); // Inside the box
      doc.text(`${total.toFixed(2)}`, boxStartX + boxWidth - 5, currentY + rowHeight - 3, {
        align: "right",
      });
    }



    // Footer Section
    const footerStartY = currentY + rowHeight + 10;

    // Receiver Name and Prepared By
    doc.setFont("helvetica", "normal");
    doc.text("Receiver Name:", 140, footerStartY + 30);
    doc.text("Prepared By:", 10, footerStartY + 30);

    // Receiver Sign & Stamp and Checked By
    doc.text("Rec Sign & Stamp:", 140, footerStartY + 40);
    doc.text("Checked By:", 10, footerStartY + 40);

    // Footer Note
    doc.setFont("helvetica", "bold");
    doc.text("Thanks, and regards", 10, footerStartY + 70);
    doc.setFont("helvetica", "normal");
    doc.text("On Behalf of Al Mamzar Car Wash. Ajman.", 10, footerStartY + 80);

    doc.setFont("helvetica", "bold");
    doc.text("Mw Wasi", 10, footerStartY + 90);
    doc.setFont("helvetica", "normal");
    doc.text("06-530-4130", 10, footerStartY + 100);
    doc.text("050-367-2862", 10, footerStartY + 110);


    // Save file based on type
    const fileName =
      type === "normal"
        ? "Normal_Invoice.pdf"
        : type === "withVAT"
          ? "Invoice_With_VAT.pdf"
          : "Invoice_Without_VAT.pdf";

    doc.save(fileName);
  };


  const generateInvoice = (type) => {
    if (filteredLedgerData.length === 0) {
      alert("No filtered data available to generate an invoice.");
      return;
    }

    const doc = new jsPDF();

    // Header Section
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text("AL MAMZAR CARS WASHING LLC", 105, 20, { align: "center" });
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Vehicle Service Station", 105, 28, { align: "center" });

    // Invoice Details
    doc.line(10, 35, 200, 35);
    doc.setFontSize(10);
    doc.text(`Date: ${new Date().toLocaleDateString("en-GB")}`, 10, 40);
    doc.setFont("helvetica", "bold");
    doc.text(`Supporting Docs`, 150, 40);

    // Our Company Details Box
    doc.setFont("helvetica", "bold");
    doc.text("AlMamzar Car Wash Details:", 10, 50);
    doc.setFont("helvetica", "normal");
    doc.text("Warehouse No. 2, P.O Box: 63798", 10, 55);
    doc.text("Jurf Industrial Zone 2, Ajman, UAE", 10, 60);
    doc.text("TRN: 104630932200003", 10, 65);
    doc.text("IBAN: AE710400008193410093901", 10, 70);
    doc.text("Tel: +971 50 367 2862", 10, 75);
    doc.text("Email: almamzarcarwash@gmail.com", 10, 80);
    doc.rect(8, 45, 95, 40);

    // Client Details Box
    doc.setFont("helvetica", "bold");
    doc.text("Client Details:", 110, 50);
    doc.setFont("helvetica", "normal");
    const { name, address, contactPerson, trnNo, contactNo } = companyInfo;
    doc.text(`Contact Person: ${contactPerson || "N/A"}`, 110, 55);

    // Add selected company name from dropdown below contact person
    doc.text(`Company: ${selectedCompany || "N/A"}`, 110, 60);

    const wrappedAddress = doc.splitTextToSize(`Address: ${address || "N/A"}`, 85);
    wrappedAddress.forEach((line, index) => doc.text(line, 110, 70 + index * 5));

    const contactPositionY = 70 + wrappedAddress.length * 5;
    doc.text(`Contact No: ${contactNo || "N/A"}`, 110, contactPositionY);

    const trnPositionY = contactPositionY + 5;
    doc.text(`TRN: ${trnNo || "N/A"}`, 110, trnPositionY);

    const rectHeight = trnPositionY - 45 + 5;
    doc.rect(108, 45, 92, rectHeight);

    // Ledger Table Header
    doc.setFont("helvetica", "bold");
    doc.text("Date", 10, 100);
    doc.text("Description", 40, 100);
    doc.text("Debit (AED)", 120, 100, { align: "right" });
    doc.text("Credit (AED)", 150, 100, { align: "right" });
    doc.text("Balance (AED)", 190, 100, { align: "right" });
    doc.line(10, 102, 200, 102);

    // Ledger Table Rows with description wrapping
    doc.setFont("helvetica", "normal");
    let startY = 110;
    filteredLedgerData.forEach((entry) => {
      const debit = parseFloat(entry.Debit) || 0;
      const credit = parseFloat(entry.Credit) || 0;
      const balance = parseFloat(entry.Balance) || 0;

      const description =
        credit > 0
          ? `Payment received from ${companyInfo.name || "the company"}`
          : entry.Description || "-";

      const wrappedDescription = doc.splitTextToSize(description, 65);

      doc.rect(10, startY - 5, 190, 10 + (wrappedDescription.length - 1) * 5);

      wrappedDescription.forEach((line, i) => {
        doc.text(line, 35, startY + i * 5);
      });

      doc.text(entry.Date || "-", 10, startY);
      doc.text(debit > 0 ? debit.toFixed(2) : "-", 120, startY, { align: "right" });
      doc.text(credit > 0 ? credit.toFixed(2) : "-", 150, startY, { align: "right" });
      doc.text(balance.toFixed(2), 190, startY, { align: "right" });
      startY += 10 + (wrappedDescription.length - 1) * 5;

      if (startY > 280) {
        doc.addPage();
        startY = 10;
        doc.setFont("helvetica", "bold");
        doc.text("Date", 10, startY);
        doc.text("Description", 40, startY);
        doc.text("Debit (AED)", 120, startY, { align: "right" });
        doc.text("Credit (AED)", 150, startY, { align: "right" });
        doc.text("Balance (AED)", 190, startY, { align: "right" });
        doc.line(10, startY + 2, 200, startY + 2);
        startY += 10;
        doc.setFont("helvetica", "normal");
      }
    });

    // Get the final balance from the last entry in the ledger
    const finalBalance = filteredLedgerData.length > 0
      ? parseFloat(filteredLedgerData[filteredLedgerData.length - 1].Balance) || 0
      : 0;

    // Calculate based on final balance
    let subtotal, vatAmount, total;

    if (type === "withVAT") {
      // For withVAT: Total = Subtotal + VAT, so Subtotal = Total / 1.05
      total = finalBalance;
      subtotal = total / 1.05;
      vatAmount = subtotal * 0.05;
    } else {
      // For normal and withoutVAT: Total = Subtotal (no VAT)
      total = finalBalance;
      subtotal = total;
      vatAmount = 0;
    }

    startY += 10;
    doc.setFont("helvetica", "bold");
    doc.text(`Subtotal: AED ${subtotal.toFixed(2)}`, 150, startY);

    if (type === "withVAT") {
      startY += 10;
      doc.text(`VAT (5%): AED ${vatAmount.toFixed(2)}`, 150, startY);

      startY += 10;
      doc.text(`Total: AED ${total.toFixed(2)}`, 150, startY);
    } else {
      startY += 10;
      doc.text(`Total: AED ${total.toFixed(2)}`, 150, startY);
    }

    startY += 20;
    doc.setFont("helvetica", "normal");
    doc.text("Prepared By: __________________", 10, startY);
    doc.text("Signed By: __________________", 140, startY);
    startY += 10;
    doc.text("Thank you for working with us!", 105, startY, { align: "center" });

    // Save file based on type with company name
    const safeCompanyName = (selectedCompany || 'Company').replace(/[^a-z0-9_-]+/gi, '_');
    const fileName =
      type === "normal"
        ? `${safeCompanyName}_Supporting_Docs_Normal.pdf`
        : type === "withVAT"
          ? `${safeCompanyName}_Supporting_Docs_With_VAT.pdf`
          : `${safeCompanyName}_Supporting_Docs_Without_VAT.pdf`;

    doc.save(fileName);
  };

  // Physical Invoice Functions
  const fetchNextInvoiceNumber = async () => {
    try {
      const response = await fetch(buildApiUrl("api/invoices/next-number"));
      const data = await response.json();
      if (data.success) {
        setNextInvoiceNumber(data.nextInvoiceNumber);
        setPhysicalInvoiceData(prev => ({
          ...prev,
          invoice_number: data.nextInvoiceNumber
        }));
      }
    } catch (error) {
      console.error("Error fetching next invoice number:", error);
    }
  };

  // Generate PDF with stamp and signature for physical invoice
  const generatePhysicalInvoicePDF = (invoiceData) => {
    if (filteredLedgerData.length === 0) {
      alert("No filtered data available to generate an invoice.");
      return;
    }

    const doc = new jsPDF();

    // Header Section
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text("AL MAMZAR CARS WASHING LLC", 105, 20, { align: "center" });
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Vehicle Service Station", 105, 28, { align: "center" });

    doc.setFont("helvetica", "bold");
    // Invoice Details
    doc.line(10, 35, 200, 35);
    doc.setFontSize(10);
    doc.text(`Date: ${new Date(invoiceData.invoice_date).toLocaleDateString("en-GB")}`, 10, 40);
    doc.text(`Invoice Month: ${invoiceMonth || "N/A"}`, 100, 40, { align: "center" });
    doc.text(`Tax Invoice No: ${invoiceData.invoice_number}`, 200, 40, { align: "right" });

    // Our Company Details Box
    doc.text("AlMamzar Car Wash Details:", 10, 50);
    doc.setFont("helvetica", "normal");
    doc.text("Warehouse No. 2, P.O Box: 63798 Jurf Ajman", 10, 55);
    doc.text("RAK Bank Account number : 8193410093901", 10, 60);
    doc.text("TRN: 104630932200003", 10, 65);
    doc.text("IBAN: AE710400008193410093901", 10, 70);
    doc.text("Tel: +971 50 367 2862", 10, 75);
    doc.text("Email: almamzarcarwash@gmail.com", 10, 80);
    doc.rect(8, 45, 95, 40);

    // Client Details Box
    doc.setFont("helvetica", "bold");
    doc.text("Client Details:", 110, 50);
    doc.setFont("helvetica", "normal");
    const { name, address, contactPerson, trnNo, contactNo } = companyInfo;
    doc.text(`Contact Person: ${contactPerson || "N/A"}`, 110, 55);

    // Add selected company name from dropdown below contact person
    doc.text(`Company: ${selectedCompany || "N/A"}`, 110, 60);

    const wrappedAddress = doc.splitTextToSize(`Address: ${address || "N/A"}`, 85);
    wrappedAddress.forEach((line, index) => doc.text(line, 110, 65 + index * 5));

    const contactPositionY = 65 + wrappedAddress.length * 5;
    doc.text(`Contact No: ${contactNo || "N/A"}`, 110, contactPositionY);

    const trnPositionY = contactPositionY + 5;
    doc.text(`TRN: ${trnNo || "N/A"}`, 110, trnPositionY);

    const rectHeight = trnPositionY - 45 + 5;
    doc.rect(108, 45, 92, rectHeight);

    // Ledger Summary Table with refined design
    const tableStartY = 100;
    const rowHeight = 10;
    const colDescriptionX = 15;
    const colAmountX = 180;
    const tableWidth = 190;

    // Draw Table Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.rect(10, tableStartY, tableWidth, rowHeight);
    doc.text("Description", colDescriptionX, tableStartY + 7);
    doc.text("Total Amount (AED)", colAmountX, tableStartY + 7, { align: "right" });

    var description = "Vehicle washing, greasing and polishing";
    doc.setFont("helvetica", "normal");
    if (companyInfo.name === "BCI International FZE") {
      description = "Container Washing";
    }

    // Get the final balance from the last entry in the ledger (same as other functions)
    const finalBalance = filteredLedgerData.length > 0
      ? parseFloat(filteredLedgerData[filteredLedgerData.length - 1].Balance) || 0
      : 0;

    // Calculate based on final balance (always withVAT for physical invoices)
    const total = finalBalance;
    const subtotal = total / 1.05;
    const vatAmount = subtotal * 0.05;

    const tableContentY = tableStartY + rowHeight;
    doc.rect(10, tableContentY, tableWidth, rowHeight);
    doc.text(description, colDescriptionX, tableContentY + 7);
    doc.text(subtotal.toFixed(2), colAmountX, tableContentY + 7, { align: "right" });

    // Total, Subtotal, VAT Boxes
    let currentY = tableContentY + rowHeight + 5;
    doc.setFont("helvetica", "bold");

    const boxWidth = 50;
    const boxStartX = colAmountX - 40;
    const boxStartY = currentY;
    const totalRowsHeight = rowHeight * 3;
    doc.rect(boxStartX, boxStartY, boxWidth, totalRowsHeight);

    // Subtotal row
    doc.line(boxStartX, currentY + rowHeight, boxStartX + boxWidth, currentY + rowHeight);
    doc.text("Subtotal:", boxStartX + 5, currentY + rowHeight - 3);
    doc.text(`${subtotal.toFixed(2)}`, boxStartX + boxWidth - 5, currentY + rowHeight - 3, { align: "right" });

    currentY += rowHeight;

    // VAT row
    doc.line(boxStartX, currentY + rowHeight, boxStartX + boxWidth, currentY + rowHeight);
    doc.text("VAT (5%):", boxStartX + 5, currentY + rowHeight - 3);
    doc.text(`${vatAmount.toFixed(2)}`, boxStartX + boxWidth - 5, currentY + rowHeight - 3, { align: "right" });

    currentY += rowHeight;

    // Total row
    doc.text("Total:", boxStartX + 5, currentY + rowHeight - 3);
    doc.text(`${total.toFixed(2)}`, boxStartX + boxWidth - 5, currentY + rowHeight - 3, { align: "right" });

    // Footer Section
    const footerStartY = currentY + rowHeight + 10;

    // Receiver Name and Prepared By
    doc.setFont("helvetica", "normal");
    doc.text("Receiver Name:", 140, footerStartY + 30);
    doc.text("Prepared By:", 10, footerStartY + 30);

    // Receiver Sign & Stamp and Checked By
    doc.text("Rec Sign & Stamp:", 140, footerStartY + 40);
    doc.text("Checked By:", 10, footerStartY + 40);

    // Add Signature Image (right of "Checked By:" text at same level, increased size)
    try {
      doc.addImage(signImage, 'PNG', 20, footerStartY + 28, 45, 22);
    } catch (error) {
      console.error("Error adding signature image:", error);
    }

    // Add Stamp Image (centered, increased size 1.5x)
    try {
      doc.addImage(stampImage, 'PNG', 87, footerStartY + 45, 37, 37);
    } catch (error) {
      console.error("Error adding stamp image:", error);
    }

    // Footer Note
    doc.setFont("helvetica", "bold");
    doc.text("Thanks, and regards", 10, footerStartY + 75);
    doc.setFont("helvetica", "normal");
    doc.text("On Behalf of Al Mamzar Car Wash. Ajman.", 10, footerStartY + 85);

    doc.setFont("helvetica", "bold");
    doc.text("Mw Wasi", 10, footerStartY + 95);
    doc.setFont("helvetica", "normal");
    doc.text("06-530-4130", 10, footerStartY + 105);
    doc.text("050-367-2862", 10, footerStartY + 115);

    // Save the PDF with company name and invoice number
    const safeCompanyName = (companyInfo.name || 'Company').replace(/[^a-z0-9_-]+/gi, '_');
    const fileName = `${safeCompanyName}_Invoice_${invoiceData.invoice_number}.pdf`;
    doc.save(fileName);
  };

  const handleSendPhysically = async () => {
    if (!selectedCompany || filteredLedgerData.length === 0) {
      alert("Please select a company and load invoice data first!");
      return;
    }

    // Calculate total amount using final balance (same as original PDF generation)
    const finalBalance = filteredLedgerData.length > 0
      ? parseFloat(filteredLedgerData[filteredLedgerData.length - 1].Balance) || 0
      : 0;
    const totalAmount = finalBalance;

    try {
      // First, fetch next invoice number
      const response = await fetch(buildApiUrl("api/invoices/next-number"));
      const data = await response.json();

      if (data.success) {
        // Create invoice data for PDF generation
        const invoiceData = {
          invoice_date: new Date().toISOString().split('T')[0],
          company_name: selectedCompany,
          invoice_number: data.nextInvoiceNumber,
          amount: totalAmount.toFixed(2),
          received_amount: "0",
          submit_by: "Physical"
        };

        // Generate PDF first (before showing modal)
        generatePhysicalInvoicePDF(invoiceData);

        // Pre-fill the modal with current data (all fields prefilled)
        setPhysicalInvoiceData({
          invoice_date: invoiceData.invoice_date,
          company_name: invoiceData.company_name,
          invoice_number: invoiceData.invoice_number,
          amount: invoiceData.amount,
          received_amount: "0",
          submit_by: "Physical"
        });

        // Show modal after PDF is generated
        setShowPhysicalInvoiceModal(true);
      } else {
        alert("Error fetching invoice number. Please try again.");
      }
    } catch (error) {
      console.error("Error in handleSendPhysically:", error);
      alert("Error generating invoice. Please try again.");
    }
  };

  const handleSimpleGenerateInvoice = async () => {
    if (!selectedCompany || filteredLedgerData.length === 0) {
      alert("Please select a company and load invoice data first!");
      return;
    }

    // Calculate total amount using final balance (same as original PDF generation)
    const finalBalance = filteredLedgerData.length > 0
      ? parseFloat(filteredLedgerData[filteredLedgerData.length - 1].Balance) || 0
      : 0;
    const totalAmount = finalBalance;

    try {
      // Fetch next invoice number (same as physical but without database insertion)
      const response = await fetch(buildApiUrl("api/invoices/next-number-simple"));
      const data = await response.json();

      if (data.success) {
        // Create invoice data for PDF generation
        const invoiceData = {
          invoice_date: new Date().toISOString().split('T')[0],
          company_name: selectedCompany,
          invoice_number: data.nextInvoiceNumber,
          amount: totalAmount.toFixed(2),
          received_amount: "0",
          submit_by: "Simple Generate"
        };

        // Generate PDF only (no database insertion)
        generatePhysicalInvoicePDF(invoiceData);

        alert(`Simple invoice generated successfully!\nInvoice Number: ${data.nextInvoiceNumber}\nPDF downloaded!\n\nNote: This invoice was not stored in the database.`);
      } else {
        alert("Error fetching invoice number. Please try again.");
      }
    } catch (error) {
      console.error("Error in handleSimpleGenerateInvoice:", error);
      alert("Error generating simple invoice. Please try again.");
    }
  };

  const handlePhysicalInvoiceSubmit = async () => {
    const { invoice_date, company_name, invoice_number, amount, received_amount, notes } = physicalInvoiceData;

    // Validate required fields
    if (!invoice_date || !company_name || !invoice_number || !amount) {
      alert("Missing required data. Please try again.");
      return;
    }

    // Calculate totals for invoice data using the same logic as original PDF generation
    // Get the final balance from the last entry in the ledger (this is the outstanding balance)
    const finalBalance = filteredLedgerData.length > 0
      ? parseFloat(filteredLedgerData[filteredLedgerData.length - 1].Balance) || 0
      : 0;

    // Total is now the final balance from ledger (same as original PDF generation)
    const totalAmount = finalBalance;
    const subtotal = totalAmount / 1.05;
    const vatAmount = subtotal * 0.05;

    // Get description based on company
    const getDescription = () => {
      if (companyInfo.name === "BCI International FZE") {
        return "Container Washing";
      }
      return "Vehicle washing, greasing and polishing";
    };

    // Prepare MINIMAL invoice data for PDF regeneration (only essential info)
    const completeInvoiceData = {
      companyInfo: {
        name: companyInfo.name,
        address: companyInfo.address,
        contactPerson: companyInfo.contactPerson,
        trnNo: companyInfo.trnNo,
        contactNo: companyInfo.contactNo
      },
      invoiceDetails: {
        invoiceNumber: invoice_number,
        invoiceDate: invoice_date,
        invoiceMonth: invoiceMonth || new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        totalAmount: totalAmount.toFixed(2),
        subtotal: subtotal.toFixed(2),
        vatAmount: vatAmount.toFixed(2),
        description: getDescription()
      },
      pdfSettings: {
        type: "withVAT",
        includeStamp: true,
        includeSignature: true
      },
      selectedCompany: selectedCompany,
      generatedAt: new Date().toISOString()
    };

    // Prepare data with notes field mapped to submit_by for database compatibility
    const dataToSend = {
      ...physicalInvoiceData,
      submit_by: notes || "-", // Use notes value or default
      invoice_data: completeInvoiceData // Include complete invoice data
    };

    try {
      const response = await fetch(buildApiUrl("api/invoices/send-physically"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToSend),
      });

      const data = await response.json();

      if (data.success) {
        // PDF already generated in handleSendPhysically - no need to generate again
        // alert(`invoice sent successfully!\nInvoice ID: ${data.invoiceId}\nStatus: ${data.data.status}\nPDF already downloaded!\n\n✅ Invoice data stored for future PDF regeneration.`);
        setShowPhysicalInvoiceModal(false);
        // Reset form
        setPhysicalInvoiceData({
          invoice_date: new Date().toISOString().split('T')[0],
          company_name: "",
          invoice_number: "",
          amount: "",
          received_amount: "0",
          notes: ""
        });
        
        // Refresh the physical invoices list if we're in company view
        if (activeView === "company") {
          fetchPhysicalInvoices();
        }
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (error) {
      console.error("Error sending physical invoice:", error);
      alert("Failed to send physical invoice. Please try again.");
    }
  };

  const handlePhysicalInvoiceInputChange = (field, value) => {
    setPhysicalInvoiceData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Radio Button Functions
  const handleViewChange = (view) => {
    setActiveView(view);
    if (view === "company") {
      fetchPhysicalInvoices();
    }
  };

  const fetchPhysicalInvoices = async () => {
    setLoadingPhysicalInvoices(true);
    try {
      const response = await fetch(buildApiUrl("api/invoices/physical-invoices"));
      const data = await response.json();
      if (data.success) {
        setPhysicalInvoices(data.data);
      } else {
        console.error("Failed to fetch physical invoices:", data.error);
        setPhysicalInvoices([]);
      }
    } catch (error) {
      console.error("Error fetching physical invoices:", error);
      setPhysicalInvoices([]);
    }
    setLoadingPhysicalInvoices(false);
  };

  // ----------------------------------------------------------------
  // PDF REGENERATION FUNCTIONS
  // ----------------------------------------------------------------

  // Function to regenerate PDF from stored invoice data
  const regeneratePDFFromStoredData = async (invoiceId) => {
    // Show loading state
    const button = document.querySelector(`button[onclick*="${invoiceId}"]`);
    const originalText = button?.textContent;
    if (button) {
      button.textContent = "🔄 Loading...";
      button.disabled = true;
    }

    try {
      console.log(`🔄 Regenerating PDF for invoice ID: ${invoiceId}`);
      
      // First check if PDF data is available
      const checkResponse = await fetch(buildApiUrl(`api/invoices/check-pdf-data/${invoiceId}`));
      const checkData = await checkResponse.json();
      
      if (!checkData.success) {
        alert(`Error: ${checkData.error}`);
        return;
      }
      
      if (!checkData.hasPdfData) {
        alert(`❌ No PDF data available for this invoice.\n\nThis invoice (${checkData.company_name} - ${checkData.invoice_number}) was created before PDF data storage was implemented.\n\nYou can only regenerate PDFs for invoices created after this feature was added.`);
        return;
      }
      
      // Fetch the stored invoice data
      const response = await fetch(buildApiUrl(`api/invoices/get-data/${invoiceId}`));
      const result = await response.json();
      
      if (!result.success) {
        alert(`Error fetching invoice data: ${result.error}`);
        return;
      }
      
      const { companyInfo, invoiceDetails, pdfSettings, selectedCompany } = result.data;
      
      console.log(`✅ Retrieved data for ${result.meta.company_name} - ${result.meta.invoice_number}`);
      
      // Regenerate PDF using stored data (no ledgerData needed anymore)
      generatePDFFromStoredData(companyInfo, invoiceDetails, pdfSettings, selectedCompany);
      
      // alert(`✅ PDF regenerated successfully!\n\nCompany: ${result.meta.company_name}\nInvoice: ${result.meta.invoice_number}\n\nPDF has been downloaded.`);
      
    } catch (error) {
      console.error('Error regenerating PDF:', error);
      alert('❌ Failed to regenerate PDF. Please try again.');
    } finally {
      // Restore button state
      if (button) {
        button.textContent = originalText || "📄 View PDF";
        button.disabled = false;
      }
    }
  };

  // Function to generate PDF from stored data (similar to generatePhysicalInvoicePDF but uses stored data)
  const generatePDFFromStoredData = (companyInfo, invoiceDetails, pdfSettings, selectedCompany) => {
    const doc = new jsPDF();

    // Header Section
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text("AL MAMZAR CARS WASHING LLC", 105, 20, { align: "center" });
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Vehicle Service Station", 105, 28, { align: "center" });

    doc.setFont("helvetica", "bold");
    // Invoice Details
    doc.line(10, 35, 200, 35);
    doc.setFontSize(10);
    doc.text(`Date: ${new Date(invoiceDetails.invoiceDate).toLocaleDateString("en-GB")}`, 10, 40);
    doc.text(`Invoice Month: ${invoiceDetails.invoiceMonth || "N/A"}`, 100, 40, { align: "center" });
    doc.text(`Tax Invoice No: ${invoiceDetails.invoiceNumber}`, 200, 40, { align: "right" });

    // Our Company Details Box
    doc.text("AlMamzar Car Wash Details:", 10, 50);
    doc.setFont("helvetica", "normal");
    doc.text("Warehouse No. 2, P.O Box: 63798 Jurf Ajman", 10, 55);
    doc.text("RAK Bank Account number : 8193410093901", 10, 60);
    doc.text("TRN: 104630932200003", 10, 65);
    doc.text("IBAN: AE710400008193410093901", 10, 70);
    doc.text("Tel: +971 50 367 2862", 10, 75);
    doc.text("Email: almamzarcarwash@gmail.com", 10, 80);
    doc.rect(8, 45, 95, 40);

    // Client Details Box
    doc.setFont("helvetica", "bold");
    doc.text("Client Details:", 110, 50);
    doc.setFont("helvetica", "normal");
    const { name, address, contactPerson, trnNo, contactNo } = companyInfo;
    doc.text(`Contact Person: ${contactPerson || "N/A"}`, 110, 55);
    doc.text(`Company: ${selectedCompany || name || "N/A"}`, 110, 60);

    const wrappedAddress = doc.splitTextToSize(`Address: ${address || "N/A"}`, 85);
    wrappedAddress.forEach((line, index) => doc.text(line, 110, 65 + index * 5));

    const contactPositionY = 65 + wrappedAddress.length * 5;
    doc.text(`Contact No: ${contactNo || "N/A"}`, 110, contactPositionY);

    const trnPositionY = contactPositionY + 5;
    doc.text(`TRN: ${trnNo || "N/A"}`, 110, trnPositionY);

    const rectHeight = trnPositionY - 45 + 5;
    doc.rect(108, 45, 92, rectHeight);

    // Ledger Summary Table
    const tableStartY = 100;
    const rowHeight = 10;
    const colDescriptionX = 15;
    const colAmountX = 180;
    const tableWidth = 190;

    // Draw Table Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.rect(10, tableStartY, tableWidth, rowHeight);
    doc.text("Description", colDescriptionX, tableStartY + 7);
    doc.text("Total Amount (AED)", colAmountX, tableStartY + 7, { align: "right" });

    // Use stored description
    doc.setFont("helvetica", "normal");
    const description = invoiceDetails.description || "Vehicle washing, greasing and polishing";

    // Use stored amounts
    const total = parseFloat(invoiceDetails.totalAmount);
    const subtotal = parseFloat(invoiceDetails.subtotal);
    const vatAmount = parseFloat(invoiceDetails.vatAmount);

    const tableContentY = tableStartY + rowHeight;
    doc.rect(10, tableContentY, tableWidth, rowHeight);
    doc.text(description, colDescriptionX, tableContentY + 7);
    doc.text(subtotal.toFixed(2), colAmountX, tableContentY + 7, { align: "right" });

    // Total, Subtotal, VAT Boxes
    let currentY = tableContentY + rowHeight + 5;
    doc.setFont("helvetica", "bold");

    const boxWidth = 50;
    const boxStartX = colAmountX - 40;
    const boxStartY = currentY;
    const totalRowsHeight = rowHeight * 3;
    doc.rect(boxStartX, boxStartY, boxWidth, totalRowsHeight);

    // Subtotal row
    doc.line(boxStartX, currentY + rowHeight, boxStartX + boxWidth, currentY + rowHeight);
    doc.text("Subtotal:", boxStartX + 5, currentY + rowHeight - 3);
    doc.text(`${subtotal.toFixed(2)}`, boxStartX + boxWidth - 5, currentY + rowHeight - 3, { align: "right" });

    currentY += rowHeight;

    // VAT row
    doc.line(boxStartX, currentY + rowHeight, boxStartX + boxWidth, currentY + rowHeight);
    doc.text("VAT (5%):", boxStartX + 5, currentY + rowHeight - 3);
    doc.text(`${vatAmount.toFixed(2)}`, boxStartX + boxWidth - 5, currentY + rowHeight - 3, { align: "right" });

    currentY += rowHeight;

    // Total row
    doc.text("Total:", boxStartX + 5, currentY + rowHeight - 3);
    doc.text(`${total.toFixed(2)}`, boxStartX + boxWidth - 5, currentY + rowHeight - 3, { align: "right" });

    // Footer Section
    const footerStartY = currentY + rowHeight + 10;

    // Receiver Name and Prepared By
    doc.setFont("helvetica", "normal");
    doc.text("Receiver Name:", 140, footerStartY + 30);
    doc.text("Prepared By:", 10, footerStartY + 30);

    // Receiver Sign & Stamp and Checked By
    doc.text("Rec Sign & Stamp:", 140, footerStartY + 40);
    doc.text("Checked By:", 10, footerStartY + 40);

    // Add Signature and Stamp if settings allow
    if (pdfSettings.includeSignature) {
      try {
        doc.addImage(signImage, 'PNG', 20, footerStartY + 28, 45, 22);
      } catch (error) {
        console.error("Error adding signature image:", error);
      }
    }

    if (pdfSettings.includeStamp) {
      try {
        doc.addImage(stampImage, 'PNG', 87, footerStartY + 45, 37, 37);
      } catch (error) {
        console.error("Error adding stamp image:", error);
      }
    }

    // Footer Note
    doc.setFont("helvetica", "bold");
    doc.text("Thanks, and regards", 10, footerStartY + 75);
    doc.setFont("helvetica", "normal");
    doc.text("On Behalf of Al Mamzar Car Wash. Ajman.", 10, footerStartY + 85);

    doc.setFont("helvetica", "bold");
    doc.text("Mw Wasi", 10, footerStartY + 95);
    doc.setFont("helvetica", "normal");
    doc.text("06-530-4130", 10, footerStartY + 105);
    doc.text("050-367-2862", 10, footerStartY + 115);

    // Save the PDF with regenerated suffix
    const safeCompanyName = (companyInfo.name || 'Company').replace(/[^a-z0-9_-]+/gi, '_');
    const fileName = `${safeCompanyName}_Invoice_${invoiceDetails.invoiceNumber}.pdf`;
    doc.save(fileName);
  };

  // WhatsApp and Email Functions
  const handleSendWhatsApp = () => {
    if (!selectedCompany || filteredLedgerData.length === 0) {
      alert("Please select a company and load invoice data first!");
      return;
    }
    setShowWhatsAppModal(true);
  };

  const handleSendEmail = () => {
    if (!selectedCompany || filteredLedgerData.length === 0) {
      alert("Please select a company and load invoice data first!");
      return;
    }

    // Pre-fill email subject and message
    const totalAmount = filteredLedgerData.reduce((sum, entry) => {
      const debit = parseFloat(entry.Debit) || 0;
      return sum + debit;
    }, 0);

    setEmailSubject(`Invoice from Al Mamzar Car Wash - ${selectedCompany}`);
    setEmailMessage(`Dear ${companyInfo.contactPerson || 'Sir/Madam'},

Please find attached the invoice for services provided by Al Mamzar Car Wash.

Invoice Details:
- Company: ${selectedCompany}
- Total Amount: AED ${totalAmount.toFixed(2)}
- Invoice Month: ${invoiceMonth || 'N/A'}

Thank you for your business.

Best regards,
Al Mamzar Car Wash Team
Tel: +971 50 367 2862
Email: almamzarcarwash@gmail.com`);

    setShowEmailModal(true);
  };

  const handleWhatsAppSubmit = async () => {
    if (!whatsappNumber.trim()) {
      alert("Please enter WhatsApp number!");
      return;
    }

    // Calculate total amount
    const totalAmount = filteredLedgerData.reduce((sum, entry) => {
      const debit = parseFloat(entry.Debit) || 0;
      return sum + debit;
    }, 0);

    // Create WhatsApp message
    const message = `*Invoice from Al Mamzar Car Wash*

Dear ${companyInfo.contactPerson || 'Sir/Madam'},

Please find the invoice details below:

🏢 *Company:* ${selectedCompany}
💰 *Total Amount:* AED ${totalAmount.toFixed(2)}
📅 *Invoice Month:* ${invoiceMonth || 'N/A'}
📋 *Services:* Vehicle washing, greasing and polishing

Thank you for your business with Al Mamzar Car Wash.

*Contact Details:*
📞 Tel: +971 50 367 2862
📧 Email: almamzarcarwash@gmail.com
📍 Warehouse No. 2, Jurf Industrial Zone 2, Ajman, UAE

Best regards,
Al Mamzar Car Wash Team`;

    // Format phone number (remove spaces, dashes, and add country code if needed)
    let formattedNumber = whatsappNumber.replace(/[\s-]/g, '');

    // Only add country code if number doesn't already have one
    if (!formattedNumber.startsWith('+')) {
      // If it starts with 971, just add the + sign
      if (formattedNumber.startsWith('971')) {
        formattedNumber = '+' + formattedNumber;
      }
      // If it starts with 0 (UAE local format), replace 0 with +971
      else if (formattedNumber.startsWith('0')) {
        formattedNumber = '+971' + formattedNumber.substring(1);
      }
      // If it's just digits without country code and looks like UAE number (9 digits), add +971
      else if (formattedNumber.length === 9 && /^[5][0-9]{8}$/.test(formattedNumber)) {
        formattedNumber = '+971' + formattedNumber;
      }
      // For other international numbers, don't add anything - let user specify
    }

    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${formattedNumber.replace('+', '')}?text=${encodeURIComponent(message)}`;

    // Open WhatsApp
    window.open(whatsappUrl, '_blank');

    // Generate PDF and store in database (same as Send Physically)
    try {
      // Get next invoice number
      const response = await fetch(buildApiUrl("api/invoices/next-number"));
      const data = await response.json();

      if (data.success) {
        const invoiceData = {
          invoice_date: new Date().toISOString().split('T')[0],
          company_name: selectedCompany,
          invoice_number: data.nextInvoiceNumber,
          amount: totalAmount.toFixed(2),
          received_amount: "0",
          submit_by: "WhatsApp"
        };

        // Generate PDF with stamp and signature
        generatePhysicalInvoicePDF(invoiceData);

        // Store in database
        const saveResponse = await fetch(buildApiUrl("api/invoices/send-physically"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(invoiceData),
        });

        const saveData = await saveResponse.json();

        if (saveData.success) {
          console.log("Invoice saved to database:", saveData.invoiceId);
        }
      }
    } catch (error) {
      console.error("Error generating PDF or saving to database:", error);
    }

    setShowWhatsAppModal(false);
    setWhatsappNumber("");
    alert("WhatsApp opened! Please send the message from WhatsApp.");
  };

  const handleEmailSubmit = async () => {
    if (!receiverEmail.trim() || !emailSubject.trim() || !emailMessage.trim()) {
      alert("Please fill in all email fields!");
      return;
    }

    // Calculate total amount
    const totalAmount = filteredLedgerData.reduce((sum, entry) => {
      const debit = parseFloat(entry.Debit) || 0;
      return sum + debit;
    }, 0);

    // Create Gmail compose URL (similar to WhatsApp web approach)
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(receiverEmail)}&su=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailMessage)}`;

    // Open Gmail in new tab (just like WhatsApp opens in new tab)
    const emailWindow = window.open(gmailUrl, '_blank');

    if (emailWindow) {
      // Success - Gmail opened
      setShowEmailModal(false);
      setReceiverEmail("");
      setEmailSubject("");
      setEmailMessage("");
      alert("📧 Gmail opened in new tab! Please login if needed and send the email.");
    } else {
      // If popup blocked, try alternative email services
      const outlookUrl = `https://outlook.live.com/mail/0/deeplink/compose?to=${encodeURIComponent(receiverEmail)}&subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailMessage)}`;

      try {
        window.open(outlookUrl, '_blank');
        setShowEmailModal(false);
        setReceiverEmail("");
        setEmailSubject("");
        setEmailMessage("");
        alert("📧 Outlook opened in new tab! Please login if needed and send the email.");
      } catch (error) {
        // Final fallback: Use mailto
        try {
          const mailtoUrl = `mailto:${receiverEmail}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailMessage)}`;
          window.location.href = mailtoUrl;

          setShowEmailModal(false);
          setReceiverEmail("");
          setEmailSubject("");
          setEmailMessage("");
          alert("📧 Email client should open now. If not, please check if you have an email application installed.");
        } catch (mailtoError) {
          // Copy to clipboard as last resort
          try {
            await navigator.clipboard.writeText(`To: ${receiverEmail}\nSubject: ${emailSubject}\n\n${emailMessage}`);
            alert("📧 Email details copied to clipboard! Please paste in your email application.");
          } catch (clipboardError) {
            alert(`📧 Please create email manually:\n\nTo: ${receiverEmail}\nSubject: ${emailSubject}\n\nMessage:\n${emailMessage}`);
          }

          setShowEmailModal(false);
          setReceiverEmail("");
          setEmailSubject("");
          setEmailMessage("");
        }
      }
    }

    // Generate PDF and store in database (same as Send Physically)
    try {
      // Get next invoice number
      const response = await fetch(buildApiUrl("api/invoices/next-number"));
      const data = await response.json();

      if (data.success) {
        const invoiceData = {
          invoice_date: new Date().toISOString().split('T')[0],
          company_name: selectedCompany,
          invoice_number: data.nextInvoiceNumber,
          amount: totalAmount.toFixed(2),
          received_amount: "0",
          submit_by: "Email"
        };

        // Generate PDF with stamp and signature
        generatePhysicalInvoicePDF(invoiceData);

        // Store in database
        const saveResponse = await fetch(buildApiUrl("api/invoices/send-physically"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(invoiceData),
        });

        const saveData = await saveResponse.json();

        if (saveData.success) {
          console.log("Invoice saved to database:", saveData.invoiceId);
        }
      }
    } catch (error) {
      console.error("Error generating PDF or saving to database:", error);
    }
  };

  return (
    <div
      className="invoices-container"
      style={{ maxHeight: "90vh", overflowY: "scroll" }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
        <h1 style={{ margin: 0 }}>Invoice Module</h1>
        <button
          onClick={() => {
            try {
              if (!filteredLedgerData || filteredLedgerData.length === 0) {
                alert('No data to export');
                return;
              }
              const headers = ['Date', 'Description', 'Debit', 'Credit', 'Balance'];
              const rows = filteredLedgerData.map((entry) => {
                const desc = (parseFloat(entry.Credit) || 0) > 0
                  ? `Payment received from ${companyInfo.name || 'the company'}`
                  : (entry.Description || '-');
                return [
                  entry.Date || '-',
                  desc,
                  entry.Debit !== undefined && entry.Debit !== null ? String(entry.Debit) : '-',
                  entry.Credit !== undefined && entry.Credit !== null ? String(entry.Credit) : '-',
                  entry.Balance !== undefined && entry.Balance !== null ? String(entry.Balance) : '-',
                ];
              });
              const csv = [headers, ...rows]
                .map(row => row.map(cell => {
                  const val = cell === null || cell === undefined ? '' : String(cell);
                  const escaped = val.replace(/\"/g, '""');
                  return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
                }).join(',')).join('\n');
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              const safeCompany = (companyInfo.name || 'Company').replace(/[^a-z0-9_-]+/gi, '_');
              a.download = `Invoice_Data_${safeCompany}_${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            } catch (e) {
              console.error('Export failed', e);
              alert('Failed to export CSV');
            }
          }}
          style={{
            background: 'none',
            border: '1px solid #ccc',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '18px',
            color: '#217346',
            padding: '6px 10px'
          }}
          title="Export current table to CSV"
        >
          📊
        </button>
      </div>

      {/* Radio Button Section */}
      <div style={{
        display: "flex",
        gap: "20px",
        marginBottom: "20px",
        padding: "15px",
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        border: "1px solid #dee2e6"
      }}>
        <label style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          fontSize: "16px",
          fontWeight: "500"
        }}>
          <input
            type="radio"
            name="invoiceView"
            value="generate"
            checked={activeView === "generate"}
            onChange={(e) => handleViewChange(e.target.value)}
            style={{ transform: "scale(1.2)" }}
          />
          📄 Generate Invoices
        </label>
        <label style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          fontSize: "16px",
          fontWeight: "500"
        }}>
          <input
            type="radio"
            name="invoiceView"
            value="company"
            checked={activeView === "company"}
            onChange={(e) => handleViewChange(e.target.value)}
            style={{ transform: "scale(1.2)" }}
          />
          🏢 Past Invoices
        </label>
      </div>

      {/* Conditional Content Based on Active View */}
      {activeView === "generate" ? (
        <>
          {/* Generate Invoices Content */}
          <div className="form-group">
            <label htmlFor="company">Company:</label>
            <select
              id="company"
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
            >
              <option value="">Select a Company</option>
              {companies.map((company) => (
                <option key={company.id} value={company.name}>
                  {company.name}
                </option>
              ))}
            </select>
            <button
              onClick={handleShowAvailedServices}
              className="show-services-button"
            >
              Show Availed Services
            </button>
          </div>

          {showInvoiceNumberField && (
            <div className="form-group">
              <label htmlFor="invoiceNumber">Invoice Number:</label>
              <input
                type="text"
                id="invoiceNumber"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Enter Invoice Number"
              />
            </div>
          )}

          {showInvoiceNumberField && (
            <div className="form-group">
              <label htmlFor="invoiceMonth">Invoice Month:</label>
              <input
                type="text"
                id="invoiceMonth"
                value={invoiceMonth}
                onChange={(e) => setInvoiceMonth(e.target.value)}
                placeholder="Enter Invoice Month"
              />
            </div>
          )}

          {ledgerData.length > 0 && (
            <div
              className="date-filter-container"
              style={{ display: "flex", alignItems: "center", gap: "10px" }}
            >
              <label>
                Start Date:
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
              <label>
                End Date:
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </label>
              <button onClick={handleFilterByDate} className="filter-button">
                Filter
              </button>
            </div>
          )}

          {loading ? (
            <p>Loading...</p>
          ) : (
            filteredLedgerData.length > 0 && (
              <>
                <div className="entries-table-container">
                  <table className="entries-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Debit</th>
                        <th>Credit</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLedgerData.map((entry, index) => {
                        const description =
                          entry.Credit > 0
                            ? `Payment received from ${companyInfo.name || "the company"}`
                            : entry.Description || "-";

                        return (
                          <tr key={index}>
                            <td>{entry.Date}</td>
                            <td>{description}</td>
                            <td>{entry.Debit ? entry.Debit : "-"}</td>
                            <td>{entry.Credit ? entry.Credit : "-"}</td>
                            <td>{entry.Balance}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="button-group">
                  {showAllButtons && (
                    <>
                      <button onClick={() => generateOnePageInvoice("normal")} className="generate-pdf-button">
                        Generate Invoice (Normal)
                      </button>
                      <button onClick={() => generateOnePageInvoice("withoutVAT")} className="generate-pdf-button">
                        Generate Invoice (Without VAT)
                      </button>
                    </>
                  )}
                  <button onClick={handleSimpleGenerateInvoice} className="generate-pdf-button">
                    Generate Invoice (With VAT)
                  </button>
                </div>

                <div className="button-group">
                  {showAllButtons && (
                    <>
                      <button onClick={() => generateInvoice("normal")} className="generate-pdf-button">
                        Supporting Docs (Normal)
                      </button>
                      <button onClick={() => generateInvoice("withoutVAT")} className="generate-pdf-button">
                        Supporting Docs (Without VAT)
                      </button>
                    </>
                  )}
                  <button onClick={() => generateInvoice("withVAT")} className="generate-pdf-button">
                    Supporting Docs (With VAT)
                  </button>
                </div>

                {/* Send Physically Button */}
                <div className="button-group" style={{ marginTop: "20px", borderTop: "2px solid #ddd", paddingTop: "15px" }}>
                  <button
                    onClick={handleSendPhysically}
                    className="generate-pdf-button"
                    style={{
                      backgroundColor: "#28a745",
                      color: "white",
                      fontWeight: "bold",
                      border: "none",
                      padding: "12px 24px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      marginRight: "10px"
                    }}
                  >
                    📤 Send Invoice
                  </button>

                  {/* Temporarily commented out - will be enabled later
                  <button
                    onClick={handleSendWhatsApp}
                    className="generate-pdf-button"
                    style={{
                      backgroundColor: "#25D366",
                      color: "white",
                      fontWeight: "bold",
                      border: "none",
                      padding: "12px 24px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      marginRight: "10px"
                    }}
                  >
                    📱 Send by WhatsApp
                  </button>

                  <button
                    onClick={handleSendEmail}
                    className="generate-pdf-button"
                    style={{
                      backgroundColor: "#007bff",
                      color: "white",
                      fontWeight: "bold",
                      border: "none",
                      padding: "12px 24px",
                      borderRadius: "6px",
                      cursor: "pointer"
                    }}
                  >
                    📧 Send by Email
                  </button>
                  */}
                </div>

              </>
            )
          )}
        </>
      ) : (
        <>
          {/* Company Invoices Content */}
          <div style={{ marginBottom: "20px" }}>
            <h2 style={{ color: "#333", marginBottom: "15px" }}>📋 Past Invoices Records</h2>

            {loadingPhysicalInvoices ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <p>Loading physical invoices...</p>
              </div>
            ) : physicalInvoices.length === 0 ? (
              <div style={{
                textAlign: "center",
                padding: "40px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #dee2e6"
              }}>
                <p style={{ color: "#6c757d", fontSize: "16px" }}>No physical invoices found.</p>
                <p style={{ color: "#6c757d", fontSize: "14px" }}>
                  Switch to "Generate Invoices" to create and send physical invoices.
                </p>
              </div>
            ) : (
              <div className="entries-table-container">
                <table className="entries-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      {/* <th>Sr No</th> */}
                      <th>Invoice Date</th>
                      <th>Company Name</th>
                      <th>Invoice Number</th>
                      <th>Amount (AED)</th>
                      {/*<th>Received Amount (AED)</th>*/}
                      {/* <th>Balance (AED)</th> */}
                      <th>Notes</th>
                      {/* <th>Status</th> */}
                      {/* <th>Created Date</th> */}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {physicalInvoices.map((invoice, index) => (
                      <tr key={invoice.sr_no}>
                        {/* <td>{invoice.sr_no}</td> */}
                        <td>{invoice.invoice_date}</td>
                        <td>{invoice.company_name}</td>
                        <td style={{ fontWeight: "bold", color: "#007bff" }}>
                          {invoice.invoice_number}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {parseFloat(invoice.amount).toFixed(2)}
                        </td>
                       {/* <td style={{ textAlign: "right" }}>
                          {parseFloat(invoice.received_amount).toFixed(2)}
                        </td>*/}
                        {/* <td style={{
                          textAlign: "right",
                          fontWeight: "bold",
                          color: parseFloat(invoice.balance) > 0 ? "#dc3545" : "#28a745"
                        }}>
                          {parseFloat(invoice.balance).toFixed(2)}
                        </td> */}
                        <td>{invoice.notes || '-'}</td>
                        {/* <td>
                          <span style={{
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            backgroundColor:
                              invoice.status === "Pending" ? "#ffc107" :
                                invoice.status === "Partially Paid" ? "#fd7e14" : "#28a745",
                            color: invoice.status === "Pending" ? "#000" : "#fff"
                          }}>
                            {invoice.status}
                          </span>
                        </td> */}
                        {/* <td style={{ fontSize: "12px", color: "#6c757d" }}>
                          {invoice.created_date}
                        </td> */}
                        <td style={{ textAlign: "center" }}>
                          <button
                            onClick={() => regeneratePDFFromStoredData(invoice.sr_no)}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: "#007bff",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: "bold",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              margin: "0 auto"
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = "#0056b3"}
                            onMouseOut={(e) => e.target.style.backgroundColor = "#007bff"}
                            title="Regenerate and download PDF from stored data"
                          >
                            📄 View PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Summary Statistics */}
                {/* <div style={{
                  marginTop: "20px",
                  padding: "15px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  border: "1px solid #dee2e6"
                }}>
                  <h4 style={{ marginBottom: "10px", color: "#333" }}>📊 Summary</h4>
                  <div style={{ display: "flex", gap: "30px", flexWrap: "wrap" }}>
                    <div>
                      <strong>Total Invoices:</strong> {physicalInvoices.length}
                    </div>
                    <div>
                      <strong>Total Amount:</strong> AED {physicalInvoices.reduce((sum, inv) => sum + parseFloat(inv.amount), 0).toFixed(2)}
                    </div>
                    <div>
                      <strong>Total Received:</strong> AED {physicalInvoices.reduce((sum, inv) => sum + parseFloat(inv.received_amount), 0).toFixed(2)}
                    </div>
                    <div>
                      <strong>Total Outstanding:</strong> AED {physicalInvoices.reduce((sum, inv) => sum + parseFloat(inv.balance), 0).toFixed(2)}
                    </div>
                  </div>
                  <div style={{ marginTop: "10px", display: "flex", gap: "20px" }}>
                    <div>
                      <strong>Pending:</strong> {physicalInvoices.filter(inv => inv.status === "Pending").length}
                    </div>
                    <div>
                      <strong>Partially Paid:</strong> {physicalInvoices.filter(inv => inv.status === "Partially Paid").length}
                    </div>
                    <div>
                      <strong>Fully Paid:</strong> {physicalInvoices.filter(inv => inv.status === "Fully Paid").length}
                    </div>
                  </div>
                </div> */}
              </div>
            )}
          </div>
        </>
      )}

      {/* Physical Invoice Modal - Simplified */}
      {showPhysicalInvoiceModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "10px",
              width: "400px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)"
            }}
          >
            <h2 style={{ marginBottom: "20px", color: "#333" }}>Send Physical Invoice</h2>

            {/* Display prefilled information */}
            <div style={{ marginBottom: "20px", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "5px" }}>
              <p style={{ margin: "5px 0", fontSize: "14px" }}><strong>Company:</strong> {physicalInvoiceData.company_name}</p>
              <p style={{ margin: "5px 0", fontSize: "14px" }}><strong>Invoice Number:</strong> {physicalInvoiceData.invoice_number}</p>
              <p style={{ margin: "5px 0", fontSize: "14px" }}><strong>Amount:</strong> AED {physicalInvoiceData.amount}</p>
              <p style={{ margin: "5px 0", fontSize: "14px" }}><strong>Date:</strong> {physicalInvoiceData.invoice_date}</p>
              <p style={{ margin: "0", fontSize: "12px", color: "#28a745" }}>✅ PDF Generated Successfully</p>
            </div>

            {/* Notes field */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                Notes (Optional):
              </label>
              <textarea
                value={physicalInvoiceData.notes || ""}
                onChange={(e) => handlePhysicalInvoiceInputChange("notes", e.target.value)}
                placeholder="Add any additional notes..."
                rows="3"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  resize: "vertical"
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowPhysicalInvoiceModal(false)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePhysicalInvoiceSubmit}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "10px",
              width: "400px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)"
            }}
          >
            <h2 style={{ marginBottom: "20px", color: "#25D366", display: "flex", alignItems: "center", gap: "10px" }}>
              📱 Send Invoice via WhatsApp
            </h2>

            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                Receiver WhatsApp Number: <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                placeholder="e.g., +971501234567 or 0501234567"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px"
                }}
              />
              <small style={{ color: "#666", fontSize: "12px" }}>
                Enter with or without country code. We'll format it automatically.
              </small>
            </div>

            <div style={{ marginBottom: "20px", padding: "10px", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
              <strong>Preview:</strong>
              <p style={{ fontSize: "12px", color: "#666", margin: "5px 0" }}>
                Invoice details will be sent to {selectedCompany} via WhatsApp with company information and total amount.
              </p>
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  setShowWhatsAppModal(false);
                  setWhatsappNumber("");
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleWhatsAppSubmit}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#25D366",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                📱 Send WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "10px",
              width: "600px",
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)"
            }}
          >
            <h2 style={{ marginBottom: "20px", color: "#007bff", display: "flex", alignItems: "center", gap: "10px" }}>
              📧 Send Invoice via Email
            </h2>

            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                Receiver Email: <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="email"
                value={receiverEmail}
                onChange={(e) => setReceiverEmail(e.target.value)}
                placeholder="e.g., company@example.com"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px"
                }}
              />
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                Subject: <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px"
                }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                Message: <span style={{ color: "red" }}>*</span>
              </label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows="8"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                  resize: "vertical"
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setReceiverEmail("");
                  setEmailSubject("");
                  setEmailMessage("");
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleEmailSubmit}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                📧 Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

export default Invoices;
